/***********************************************
ICEMEDX - Developed By ICEMEDX, Inc.
Copyright 2018 ICEMEDX, Inc.
***********************************************/
import { SimpleChange } from '@angular/core';
import { Component, OnInit, Input, Injectable, EventEmitter, Output, Inject, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { IcecontrolSettings, IcecontrolPermission, iceColumnDefinition } from '../../components/models/icesettings';
import { userTypes } from '../../login/login.config';
import { Iceutility } from '../../services/iceutility';
import { IcematgridComponent } from '../icepanel/icematgrid.component';
import { IcepanelComponent } from '../icepanel/icepanel.component';

@Component({
	selector: 'iceinput',
	templateUrl: './iceinput.component.html',
  styleUrls: ['./iceinput.component.scss'],
  providers: [IcematgridComponent]
})
@Injectable()

export class IceinputComponent implements OnInit {
  // Input Members
  @Input() placeholder: string = "";
  @Input() id: string = "";
  @Input() properties: any = {};
  @Input() ctrl: IcecontrolSettings = null;
  @Input() tableColumn: iceColumnDefinition = null;
  @Input() tableRowData: any = null;
  @Input() matTableObject: IcematgridComponent = null;

  // Output Members
  @Output() InputChanged: EventEmitter<any> = new EventEmitter<any>();
  @Output() keydownEnter: EventEmitter<any> = new EventEmitter<any>();
  @Output() keydownTab: EventEmitter<any> = new EventEmitter<any>();
  @Output() keyUp: EventEmitter<any> = new EventEmitter<any>();

  // Data Members
  iceFormControl: FormControl = null;
  permission: IcecontrolPermission = null;
  inputType: string = "text";

  constructor(@Inject(IcepanelComponent) private parent: IcepanelComponent) {
  }

  ngOnChanges(obj) {
    if (Iceutility.isdefined(obj.properties)) {
      let properties = obj.properties;

      if (!properties.firstChange) {
        if (properties.currentValue.inputType !== properties.previousValue.inputType) {
          this.inputType = properties.currentValue.inputType;
        }
      }
    }
  }

  ngOnInit() {
    try {
      if (Iceutility.isdefined(this.properties.inputType)) {
        this.inputType = this.properties.inputType;
      }

      this.permission = this.ctrl.permissions ? this.ctrl.permissions : new IcecontrolPermission(userTypes.USER_FEATURES.ufNone, userTypes.USER_FEATURE_ACCESS.ufaNone);
      if (this.matTableObject && this.tableRowData != null && this.tableColumn != null) {
        // tables should not use the standard formcontrol
        let formRowGroup: FormGroup = this.matTableObject.getFormGroup(this.tableRowData);
        this.iceFormControl = new FormControl(this.tableRowData[this.tableColumn.columnName]);
        formRowGroup.addControl(this.tableColumn.columnName, this.iceFormControl);
      }
      else if (this.ctrl == null) {
        this.iceFormControl = new FormControl(''); // No control so we create a placeholder control to prevent an errors
      }
      else {
        this.iceFormControl = this.ctrl.getFormControl();
        if (this.iceFormControl == null)
          this.iceFormControl = new FormControl(''); // No control so we create a placeholder control to prevent an errors
      }

      if (this.permission.default_access === userTypes.USER_FEATURE_ACCESS.ufaReadOnly) {
        this.iceFormControl.disable();
      }
    }
    catch (e) {
      console.log(e);
    }
  }

  ngOnDestroy() {
  }

  onInputChange(event, index) {
    if (this.tableRowData != null && this.tableColumn != null) {
      event.changeSettings = this.tableColumn;
      event.tableColumn = this.tableColumn;
      event.tableRowData = this.tableRowData;
    }
    this.InputChanged.emit(event);

    // emit to control observers
    if (this.ctrl.getChangeControlEvent() != null) {
      this.ctrl.getChangeControlEvent().emit(event);
    }
  }

  onKeyup(event): void {
    if (this.tableRowData != null && this.tableColumn != null && this.parent && this.parent.controlChange) {
      event.keyUpSettings = this.tableColumn;
      this.tableRowData[this.tableColumn.columnName] = event.currentTarget.value;
      event.tableColumn = this.tableColumn;
      event.tableRowData = this.tableRowData;
      this.parent.onKeyUp(event);
    }
    else if (this.parent && (this.parent.sections != null && this.parent.sections.length > 0) && this.parent.controlChange) {
      event.keyUpSettings = this.ctrl;
      this.parent.onKeyUp(event);
    }
    else {
      event.keyUpSettings = this.ctrl;
      this.keyUp.emit(event);
    }

    // emit to control observers
    if (this.ctrl.getKeyUpControlEvent() != null) {
      this.ctrl.getKeyUpControlEvent().emit(event);
    }
  }

  public onKeydown(event): void {
    switch (event.key) {
      case "Enter": {
        if (this.tableRowData != null && this.tableColumn != null && this.parent && this.parent.keydownEnter) {
          event.keydownEnterSettings = this.tableColumn;
          this.tableRowData[this.tableColumn.columnName] = event.currentTarget.value;
          event.tableColumn = this.tableColumn;
          event.tableRowData = this.tableRowData;
          this.parent.onKeydownEnter(event);
        }
        else if (this.parent && (this.parent.sections != null && this.parent.sections.length > 0) && this.parent.keydownEnter) {
          event.keydownEnterSettings = this.ctrl;
          this.parent.onKeydownEnter(event);
        }
        else {
          event.keydownEnterSettings = this.ctrl;
          this.keydownEnter.emit(event);
        }
        break;
      }
      case "Tab": {
        if (this.tableRowData != null && this.tableColumn != null && this.parent && this.parent.keydownTab) {
          event.keydownTabSettings = this.tableColumn;
          this.tableRowData[this.tableColumn.columnName] = event.currentTarget.value;
          event.tableColumn = this.tableColumn;
          event.tableRowData = this.tableRowData;
          this.parent.onKeydownTab(event);
        }
        else if (this.parent && (this.parent.sections != null && this.parent.sections.length > 0) && this.parent.keydownTab) {
          event.keydownTabSettings = this.ctrl;
          this.parent.onKeydownTab(event);
        }
        else {
          event.keydownTabSettings = this.ctrl;
          this.keydownTab.emit(event);
        }
        break;
      }
    }

    // emit to control observers
    if (this.ctrl.getKeyDownControlEvent() != null) {
      this.ctrl.getKeyDownControlEvent().emit(event);
    }
  }

  isReadonly(): boolean {
    if (this.ctrl == null) return false;

    return this.ctrl.getReadonly();
  }

  isDisabled(): boolean {
    if (this.ctrl == null || this.permission == null) return false;

    if (this.ctrl.getDisabled()) return true;

    return this.permission.default_access === userTypes.USER_FEATURE_ACCESS.ufaReadOnly;
  }

  getValue(): any {
    if (this.iceFormControl == null) return "";
    return this.iceFormControl.value;
  }

  public getStyle(): any {
    let style = {
      "width": "100px",
    };

    if (this.ctrl == null || this.ctrl.properties == null) return style;

    if (this.ctrl.properties.width != null) {
      style.width = this.ctrl.properties.width;
    }
    return style;
  }
}
